import api, { CheckDirectionStatus } from '@/api/facetracker/FaceTracker';
import {
  FaceResult,
  CalibrationResult,
  CalibrationStatus,
  DirectionDetectionPhase,
  ErrorType,
  TakeSelfieResult,
  InitializeDirectionsResult,
  ApiErrorResponse,
  ApiErrorCode
} from '.';
import { setTimeoutAsync } from '@/services/utils';
import { AxiosError } from 'axios';
import { OsType, getOSType } from '@/util/device';

export default class FaceTrackerServer {
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private sessionID: number = 0;

  constructor(video: HTMLVideoElement, canvas: HTMLCanvasElement) {
    this.video = video;
    this.canvas = canvas;
  }

  async load(): Promise<void> {
    //
  }

  dispose(): void {
    //
  }

  async videoInitialized(width: number, height: number): Promise<boolean> {
    try {
      const ret = await api.start();
      this.sessionID = ret.data.faceActionAnimationId;
      return true;
    } catch (error) {
      return false;
    }
  }

  async videoReInitialized(): Promise<boolean> {
    try {
      const ret = await api.start();
      this.sessionID = ret.data.faceActionAnimationId;
      return true;
    } catch (error) {
      return false;
    }
  }

  async calibrate(): Promise<CalibrationResult> {
    const base64 = this.captureToBase64();
    const result: CalibrationResult = {
      status: CalibrationStatus.Processing,
      currentCount: 0,
      maxCount: 1
    };
    try {
      const res = await api.calibrate({ id: this.sessionID, imageBase64: base64 });
      result.status = res.data.isCompleted ? CalibrationStatus.Completed : CalibrationStatus.Processing;
      result.currentCount = res.data.currentCount;
      result.maxCount = res.data.maxCount;
    } catch (error) {
      result.status = CalibrationStatus.InError;
      result.error = this.getErrorType(error);
      result.isManyRetry = this.isManyRetry(error);
      await setTimeoutAsync(500);
    }
    return result;
  }

  async takeSelfie(): Promise<TakeSelfieResult> {
    const base64 = this.captureToBase64(false);
    try {
      await api.takeSelfie({ id: this.sessionID, imageBase64: base64 });
      return { isSuccess: true };
    } catch (error) {
      return { isSuccess: false, error: this.getErrorType(error) };
    }
  }

  async initializeDirections(): Promise<InitializeDirectionsResult> {
    try {
      const res = await api.initializeDirections(this.sessionID);
      return { directions: res.data.directions };
    } catch (error) {
      return { directions: [], error: this.getErrorType(error) };
    }
  }

  async detect(phase: DirectionDetectionPhase): Promise<FaceResult> {
    const result: FaceResult = {
      result: CheckDirectionStatus.None
    };
    try {
      // upload and return result
      const base64 = this.captureToBase64();
      const res = await api.detectFace({ id: this.sessionID, imageBase64: base64, phase });
      switch (res.data.result) {
        case CheckDirectionStatus.Success:
        case CheckDirectionStatus.Ignore:
          result.result = res.data.result;
          break;
        case CheckDirectionStatus.FaceNotFound:
          result.result = res.data.result;
          result.error = ErrorType.FaceNotFound;
          break;
        case CheckDirectionStatus.DifferentDirection:
          result.result = res.data.result;
          break;
        default:
          break;
      }
    } catch (error) {
      result.result = CheckDirectionStatus.InError;
      result.error = this.getErrorType(error);
      result.isManyRetry = this.isManyRetry(error);
      await setTimeoutAsync(500);
    } finally {
      //
    }
    return result;
  }

  get renderCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  public captureToBase64(needsMinify: boolean = true): string {
    const isPC: boolean = getOSType() === OsType.PC;
    const MAX_CANVAS_SHORTEST_SIZE = isPC ? 240 : 160;

    if (!needsMinify) {
      this.canvas.width = this.video.videoWidth;
      this.canvas.height = this.video.videoHeight;
    } else if (this.video.videoWidth < this.video.videoHeight) {
      this.canvas.width = MAX_CANVAS_SHORTEST_SIZE;
      this.canvas.height = (MAX_CANVAS_SHORTEST_SIZE * this.video.videoHeight) / this.video.videoWidth;
    } else {
      this.canvas.width = (MAX_CANVAS_SHORTEST_SIZE * this.video.videoWidth) / this.video.videoHeight;
      this.canvas.height = MAX_CANVAS_SHORTEST_SIZE;
    }

    // videoとcanvasのアスペクト比は同じになるのでここの処理は冗長の様子
    const srcAspect = this.video.videoWidth / this.video.videoHeight;
    const destAspect = this.canvas.width / this.canvas.height;
    const ctx = this.canvas.getContext('2d')!;
    if (srcAspect < destAspect) {
      const h = this.video.videoWidth / destAspect;
      const y = (this.video.videoHeight - h) / 2;
      ctx.drawImage(this.video, 0, y, this.video.videoWidth, h, 0, 0, this.canvas.width, this.canvas.height);
    } else {
      const w = this.video.videoHeight * destAspect;
      const x = (this.video.videoWidth - w) / 2;
      ctx.drawImage(this.video, x, 0, w, this.video.videoHeight, 0, 0, this.canvas.width, this.canvas.height);
    }

    const dataUrl = this.canvas.toDataURL('image/jpeg', 0.8);
    return dataUrl.split(',')[1];
  }

  private getErrorType(error: any): ErrorType {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      // API Error
      const response = axiosError.response.data as ApiErrorResponse;
      if (response.error_code) {
        switch (response.error_code) {
          case ApiErrorCode.CalibrationTimeout:
          case ApiErrorCode.CalibrationTimeoutWithManyRetry:
          case ApiErrorCode.DetectionTimeout:
          case ApiErrorCode.DetectionTimeoutWithManyRetry:
            return ErrorType.Timeout;

          case ApiErrorCode.FaceNotFound:
          case ApiErrorCode.FaceNotFoundWithManyRetry:
            return ErrorType.FaceNotFound;

          case ApiErrorCode.DifferentDirection:
          case ApiErrorCode.DifferentDirectionWithManyRetry:
            return ErrorType.DifferentDirection;

          default:
            return ErrorType.Unknown;
        }
      }

      if (error.message.includes('timeout')) {
        return ErrorType.Timeout;
      } else if (error.response) {
        return ErrorType.ServerError;
      } else if (error.message === 'Network Error') {
        return ErrorType.Network;
      }
    } else {
      // Unknown error
      console.error(error); // eslint-disable-line no-console
      return ErrorType.Unknown;
    }

    return ErrorType.Unknown;
  }

  private isManyRetry(error: any): boolean {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      // API Error
      const response = axiosError.response.data as ApiErrorResponse;
      if (response.error_code) {
        switch (response.error_code) {
          case ApiErrorCode.CalibrationTimeoutWithManyRetry:
          case ApiErrorCode.DetectionTimeoutWithManyRetry:
          case ApiErrorCode.DifferentDirectionWithManyRetry:
          case ApiErrorCode.FaceNotFoundWithManyRetry:
            return true;
          default:
            return false;
        }
      }
    }
    return false;
  }
}
